import { useSyncExternalStore } from 'react';

import { travaDisponivel } from './chaveDoAparelho.js';

/**
 * A trava local do app: com ela ligada, abrir o Sheafyield pede a chave do
 * aparelho (Face ID) antes de mostrar qualquer coisa.
 *
 * O que ela é: um **portão de conveniência**. A partir da etapa 6 o aparelho
 * carrega nomes e endereços de pessoas reais, e a trava é o que impede que
 * um celular emprestado por dois minutos vire uma lista de moradores. O
 * login não muda: falhar no Face ID **não** desloga — tenta de novo.
 *
 * O que ela não é: criptografia. Os dados continuam em claro no IndexedDB, e
 * quem tiver o aparelho desbloqueado e um depurador os lê sem passar por
 * aqui. Prometer mais do que isto na tela seria mentira.
 *
 * **A preferência mora no IndexedDB, junto do que ela guarda.** Morava no
 * `localStorage`, e isso era um defeito grave: o Safari descarta
 * `localStorage` sob pressão de espaço, enquanto os dados protegidos — e a
 * sessão — vivem no IndexedDB e sobrevivem. A evicção, portanto, apagava a
 * opção e **abria o app sozinha**: a trava falhava para o lado errado. Agora
 * ela mora na mesma tabela `estado` da sessão: ou os dois somem, ou nenhum
 * dos dois.
 *
 * **O estado da trava continua sem ser gravado.** Se "destrancado" morasse
 * no armazenamento, limpá-lo abriria o app escancarado. O padrão é
 * calculado, não lido: opção ligada, app trancado. Destrancar vive só na
 * memória desta página; recarregar tranca de novo.
 *
 * **E, enquanto a preferência não chegou do banco, o portão fica fechado.**
 * Ler o IndexedDB é assíncrono, e nascer aberto "só até responder" seria uma
 * janela em que o conteúdo monta — curta no Mac, não necessariamente curta
 * num iPhone antigo com o disco cheio.
 */

export type Tolerancia = 'imediatamente' | '1min' | '5min';

/** Quanto tempo em segundo plano a trava perdoa. `imediatamente` é zero: a
 *  volta já pede a chave. */
export const MS_DA_TOLERANCIA: Readonly<Record<Tolerancia, number>> = {
  imediatamente: 0,
  '1min': 60_000,
  '5min': 300_000,
};

/** A chave na tabela `estado` do Dexie — a mesma em que a sessão do Supabase
 *  mora, com o prefixo `auth:`. */
export const CHAVE = 'trava';

export function ehTolerancia(v: unknown): v is Tolerancia {
  return v === 'imediatamente' || v === '1min' || v === '5min';
}

export interface Preferencia {
  ligada: boolean;
  tolerancia: Tolerancia;
}

export const DESLIGADA: Preferencia = {
  ligada: false,
  tolerancia: 'imediatamente',
};

export interface EstadoDaTrava extends Preferencia {
  /** `true` = nada de conteúdo na tela até a chave passar. */
  trancado: boolean;
  /** A preferência já veio do banco? Enquanto for `false`, `trancado` é
   *  `true` por não saber — e a tela mostra uma espera, não o botão. */
  carregada: boolean;
}

/**
 * O tanto de banco que a preferência exige: uma tabela de chave e valor.
 *
 * Declarado como forma, e não importado como `Banco`, para o teste passar um
 * dublê de três linhas e para este módulo não arrastar o Dexie para dentro
 * de cada suíte que só quer a regra.
 */
export interface ArmazemDaPreferencia {
  get(chave: string): Promise<{ valor: string } | undefined>;
  put(linha: { chave: string; valor: string }): Promise<unknown>;
  delete(chave: string): Promise<unknown>;
}

/** Quanto se espera antes da segunda tentativa de leitura. Curto de
 *  propósito: o portão está fechado enquanto isso, e o usuário vê a tela de
 *  espera. */
const MS_ENTRE_TENTATIVAS = 50;

const PAUSA_PADRAO = (ms: number): Promise<void> =>
  new Promise((resolver) => setTimeout(resolver, ms));

/**
 * A ausência da linha é a opção desligada — o mesmo padrão do tema e da
 * escala. Guardar o valor "desligado" só criaria lixo que sobreviveria a
 * uma mudança futura do padrão.
 *
 * **Duas tentativas, e só então "desligada".** Ler pode falhar por dois
 * motivos bem diferentes: o banco não existe mais (janela privada, evicção,
 * cota) — e aí não há dado a proteger, responder "desligada" é o certo — ou
 * o banco existe e está momentaneamente fora do ar: um
 * `DatabaseClosedError` porque outra aba está fazendo o upgrade do esquema,
 * uma transação abortada sob pressão. Nesse segundo caso os dados estão lá,
 * e desistir na primeira falha abre o app sobre eles. A segunda tentativa,
 * depois de uma pausa curta, é o que separa um caso do outro na prática.
 *
 * O que ela **não** promete: distinguir os dois com certeza, nem esperar um
 * upgrade demorado. Falhar duas vezes ainda abre.
 */
export async function lerPreferencia(
  armazem: ArmazemDaPreferencia,
  esperar: (ms: number) => Promise<void> = PAUSA_PADRAO,
): Promise<Preferencia> {
  for (const ultima of [false, true]) {
    try {
      const linha = await armazem.get(CHAVE);
      return ehTolerancia(linha?.valor)
        ? { ligada: true, tolerancia: linha.valor }
        : DESLIGADA;
    } catch {
      if (!ultima) await esperar(MS_ENTRE_TENTATIVAS);
    }
  }
  return DESLIGADA;
}

export async function gravarPreferencia(
  armazem: ArmazemDaPreferencia,
  p: Preferencia,
): Promise<void> {
  try {
    if (p.ligada) await armazem.put({ chave: CHAVE, valor: p.tolerancia });
    else await armazem.delete(CHAVE);
  } catch {
    // Não poder lembrar não impede valer nesta sessão.
  }
}

/**
 * Voltando ao app depois de `saiuEm`, a trava fecha?
 *
 * Pura, e por isso a regra inteira cabe num teste sem relógio nem DOM.
 *
 * Dois casos que respondem "tranca" por prudência, não por conta de tempo:
 * não saber quando o app saiu (`null`) não é prova de que ele acabou de
 * sair; e um relógio que andou para trás não é conta em que se confie.
 */
export function deveTrancar(
  saiuEm: number | null,
  agora: number,
  tolerancia: Tolerancia,
): boolean {
  if (saiuEm === null) return true;
  const decorrido = agora - saiuEm;
  if (!Number.isFinite(decorrido) || decorrido < 0) return true;
  return decorrido >= MS_DA_TOLERANCIA[tolerancia];
}

/**
 * A trava como uma pequena loja observável, no mesmo formato do motor de
 * sincronização: `estado()` devolve um instantâneo estável e `assinar()`
 * avisa quem depende dele.
 *
 * É classe, e não um punhado de variáveis de módulo, para que cada teste
 * tenha a sua — estado de módulo vazaria de um teste para o seguinte.
 */
export class Trava {
  #preferencia: Preferencia = DESLIGADA;
  #carregada = false;
  /** Fechado por não saber: o instante entre montar e o banco responder não
   *  é hora de mostrar nome de morador. */
  #trancado = true;
  #saiuEm: number | null = null;
  /** O app está fora de vista? Nasce `true` porque o app que acaba de
   *  carregar veio de fora — e é o que faz a primeira volta trancar. Existe
   *  para que **dois** eventos de volta (`pageshow` e `visibilitychange`
   *  chegam juntos numa restauração) não virem duas decisões: a segunda
   *  encontraria `saiuEm` já consumido, leria `null` e trancaria um app que
   *  a primeira acabara de perdoar dentro da tolerância. */
  #fora = true;
  #instantaneo: EstadoDaTrava;
  readonly #ouvintes = new Set<() => void>();

  /**
   * Sem argumento, nasce fechada e à espera de `carregar`. Com uma
   * preferência, nasce já sabida — é como os testes e a injeção da suíte
   * de ponta a ponta a constroem.
   */
  constructor(preferencia?: Preferencia) {
    if (preferencia !== undefined) {
      this.#preferencia = preferencia;
      this.#carregada = true;
      this.#trancado = preferencia.ligada;
    }
    this.#instantaneo = this.#montar();
  }

  #montar(): EstadoDaTrava {
    return {
      ...this.#preferencia,
      trancado: this.#trancado,
      carregada: this.#carregada,
    };
  }

  /** Estável enquanto nada muda: `useSyncExternalStore` compara por
   *  identidade e um literal novo a cada chamada é laço infinito. */
  estado = (): EstadoDaTrava => this.#instantaneo;

  assinar = (ouvinte: () => void): (() => void) => {
    this.#ouvintes.add(ouvinte);
    return () => this.#ouvintes.delete(ouvinte);
  };

  #publicar(): void {
    this.#instantaneo = this.#montar();
    for (const ouvinte of this.#ouvintes) ouvinte();
  }

  /**
   * Lê a preferência do banco e abre o portão se ela estiver desligada.
   *
   * `disponivel` é a segunda guarda, e ela não é detalhe: sem WebAuthn nesta
   * origem, "Desbloquear" nunca daria certo e não haveria caminho até
   * Ajustes para desligar a opção. É o mesmo portão que a Conta usa para
   * **oferecer** a opção; aplicá-lo só lá deixava a trava valendo onde ela
   * não abre.
   */
  carregar = async (
    armazem: ArmazemDaPreferencia,
    disponivel: boolean = travaDisponivel(),
  ): Promise<void> => {
    const lida = await lerPreferencia(armazem);
    this.#preferencia = disponivel ? lida : { ...lida, ligada: false };
    this.#carregada = true;
    this.#trancado = this.#preferencia.ligada;
    this.#publicar();
  };

  /**
   * Liga, desliga ou muda a tolerância — e grava a escolha.
   *
   * Ligar **não** tranca na hora: quem está mexendo em Ajustes acabou de
   * usar o app, e trancá-lo no meio do toque seria castigo, não segurança.
   * A opção vale da próxima vez que o app abrir ou voltar. Desligar, sim,
   * destranca: não faz sentido manter preso o que deixou de ser trancado.
   *
   * **O armazém é exigido, não herdado.** Ele já foi opcional, caindo no
   * que `carregar` tivesse guardado — e assim uma tela de Ajustes montada
   * sem `carregar` antes mostrava a opção ligando e desligando de mentira:
   * o estado mudava na tela e a gravação ia para lugar nenhum, em silêncio,
   * até o próximo recarregamento desfazer tudo. Exigir o argumento faz o
   * compilador cobrar a ligação, e um refatoramento futuro não consegue
   * produzir aquela tela.
   */
  configurar = async (
    p: Preferencia,
    armazem: ArmazemDaPreferencia,
  ): Promise<void> => {
    this.#preferencia = p;
    this.#carregada = true;
    if (!p.ligada) this.#trancado = false;
    this.#publicar();
    // Grava depois de publicar: a tela responde ao toque no mesmo quadro, e
    // o IndexedDB alcança.
    await gravarPreferencia(armazem, p);
  };

  trancar = (): void => {
    if (this.#trancado) return;
    this.#trancado = true;
    this.#publicar();
  };

  destrancar = (): void => {
    this.#saiuEm = null;
    this.#fora = false;
    if (!this.#trancado) return;
    this.#trancado = false;
    this.#publicar();
  };

  /** O app foi para o segundo plano. Só um carimbo em memória: **nada** de
   *  armazenamento aqui (nem em `pagehide`, que é armadilha conhecida —
   *  travar a tela o dispara e um web clip não entra no bfcache). */
  saiu = (agora: number = Date.now()): void => {
    this.#fora = true;
    this.#saiuEm = agora;
  };

  /** O app voltou. Passou da tolerância, tranca. Chamar duas vezes seguidas
   *  é o caso normal (dois eventos para a mesma volta) e a segunda não
   *  decide nada. */
  voltou = (agora: number = Date.now()): void => {
    if (!this.#preferencia.ligada) return;
    if (!this.#fora) return;
    this.#fora = false;
    if (deveTrancar(this.#saiuEm, agora, this.#preferencia.tolerancia))
      this.trancar();
    this.#saiuEm = null;
  };
}

/** A trava do app. Uma só, porque o aparelho é um só. */
export const trava = new Trava();

/**
 * Liga a trava ao ciclo de vida da página.
 *
 * `visibilitychange` é o evento que o iOS dispara ao mandar o app para o
 * segundo plano e ao trazê-lo de volta sem recarregar — o caso do web clip
 * instalado. `pageshow` cobre o outro caminho de volta: a página restaurada
 * (bfcache no Safari comum, ou a aba redesenhada depois de o sistema
 * descarregá-la), em que `visibilitychange` pode não vir. Ele **só chama
 * `voltou`**, que nunca solta nada — a armadilha é soltar armazenamento na
 * saída, e a saída aqui continua sendo só um carimbo em memória.
 */
export function observarSegundoPlano(
  t: Trava = trava,
  doc: Document = document,
  janela: Pick<Window, 'addEventListener' | 'removeEventListener'> = window,
): () => void {
  const aoMudar = (): void => {
    if (doc.hidden) t.saiu();
    else t.voltou();
  };
  const aoRestaurar = (): void => {
    t.voltou();
  };
  doc.addEventListener('visibilitychange', aoMudar);
  janela.addEventListener('pageshow', aoRestaurar);
  return () => {
    doc.removeEventListener('visibilitychange', aoMudar);
    janela.removeEventListener('pageshow', aoRestaurar);
  };
}

export function useTrava(t: Trava = trava): EstadoDaTrava {
  return useSyncExternalStore(t.assinar, t.estado, t.estado);
}
